import {
  useQuery,
  useMutation,
  useQueryClient,
  useQueries,
} from '@tanstack/react-query';
import { useMemo } from 'react';
import {
  listOrganizationPolicies,
  toggleOrganizationPolicy,
  getOrganizationPolicySettings,
  updateOrganizationPolicySettings,
  deleteOrganizationPolicySettings,
} from '@kirin/api-client/api/policies';
import type { OrganizationId } from '@kirin/api-client/contracts/organizations';
import { apiClient } from '@/lib/api-client';
import { queryKeys } from './queryKeys';
import { cacheConfigForCollaborativeData } from './queryConfig';
import { retryHandlers } from './helpers';
import type {
  PolicySettings,
  PolicySettingsUpdate,
  PolicyModeType,
} from '@kirin/api-client/contracts/policies';

/**
 * Hook to fetch organization policies list
 *
 * Uses React Query for caching and automatic refetching.
 * Uses collaborative cache config: refetches on window focus to catch changes from other admins.
 *
 * @param organizationId - The organization ID to fetch policies for
 * @param enabled - Whether to enable the query (default: true if organizationId provided)
 *
 * @example
 * const { data, isLoading, error } = useOrganizationPolicies(orgId);
 *
 * @returns React Query result with policies array
 */
export function useOrganizationPolicies(
  organizationId: OrganizationId | undefined,
  enabled = true
) {
  const isEnabled = enabled && !!organizationId;
  return useQuery({
    queryKey: queryKeys.policies.organization(organizationId ?? ''),
    queryFn: async () => {
      if (!organizationId) throw new Error('organizationId is required');
      const result = await listOrganizationPolicies(apiClient, organizationId);
      return result.policies;
    },
    enabled: isEnabled,
    ...cacheConfigForCollaborativeData,
    retry: retryHandlers.list,
  });
}

/**
 * Hook to fetch policy settings for an organization policy
 *
 * @param organizationId - The organization ID
 * @param policyKey - The policy key to fetch settings for
 * @param enabled - Whether to enable the query (default: true if both IDs provided)
 *
 * @example
 * const { data: settings, isLoading } = useOrganizationPolicySettings(orgId, 'mcp-server-sources');
 *
 * @returns React Query result with policy settings
 */
export function useOrganizationPolicySettings(
  organizationId: OrganizationId | undefined,
  policyKey: string | undefined,
  enabled = true
) {
  const isEnabled = enabled && !!organizationId && !!policyKey;
  return useQuery<PolicySettings>({
    queryKey: queryKeys.policies.organizationSettings(
      organizationId ?? '',
      policyKey ?? ''
    ),
    queryFn: async () => {
      if (!organizationId) throw new Error('organizationId is required');
      if (!policyKey) throw new Error('policyKey is required');
      const result = await getOrganizationPolicySettings(
        apiClient,
        organizationId,
        policyKey
      );
      return result;
    },
    enabled: isEnabled,
    ...cacheConfigForCollaborativeData,
    retry: retryHandlers.detail,
  });
}

/**
 * Hook to fetch policy settings for multiple organization policies
 *
 * Uses React Query useQueries for fetching multiple settings in parallel.
 * Returns a map of policyKey -> settings data and loading states.
 *
 * @param organizationId - The organization ID
 * @param policyKeys - Array of policy keys to fetch settings for
 * @param enabled - Whether to enable the queries (default: true)
 *
 * @example
 * const { policySettings, loadingSettings, isLoading } = useOrganizationPolicySettingsList(orgId, ['policy1', 'policy2']);
 *
 * @returns Object with policySettings map, loadingSettings set, and isLoading flag
 */
export function useOrganizationPolicySettingsList(
  organizationId: OrganizationId | undefined,
  policyKeys: string[],
  enabled = true
) {
  const queries = useQueries({
    queries: policyKeys.map(policyKey => ({
      queryKey: queryKeys.policies.organizationSettings(
        organizationId ?? '',
        policyKey
      ),
      queryFn: async () => {
        if (!organizationId) throw new Error('organizationId is required');
        const result = await getOrganizationPolicySettings(
          apiClient,
          organizationId,
          policyKey
        );
        return result;
      },
      enabled: enabled && !!organizationId && !!policyKey,
      ...cacheConfigForCollaborativeData,
      retry: retryHandlers.detail,
    })),
  });

  const policySettings = useMemo(() => {
    const settings: Record<string, PolicySettings> = {};
    policyKeys.forEach((policyKey, index) => {
      const query = queries[index];
      if (query?.data) {
        settings[policyKey] = query.data;
      }
    });
    return settings;
  }, [policyKeys, queries]);

  const loadingSettings = useMemo(() => {
    const loading = new Set<string>();
    policyKeys.forEach((policyKey, index) => {
      const query = queries[index];
      if (query?.isLoading || query?.isFetching) {
        loading.add(policyKey);
      }
    });
    return loading;
  }, [policyKeys, queries]);

  const isLoading = queries.some(query => query.isLoading || query.isFetching);

  return {
    policySettings,
    loadingSettings,
    isLoading,
  };
}

/**
 * Hook to toggle an organization policy
 *
 * Uses React Query mutation with automatic cache invalidation.
 * Only available to owners and managers.
 *
 * @param organizationId - The organization ID
 *
 * @example
 * const { mutateAsync, isPending } = useToggleOrganizationPolicy(orgId);
 * await mutateAsync({ policyKey: 'mcp-server-sources', enabled: true });
 *
 * @returns React Query mutation result
 */
export function useToggleOrganizationPolicy(
  organizationId: OrganizationId | undefined
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      policyKey,
      enabled,
      mode,
    }: {
      policyKey: string;
      enabled: boolean;
      mode?: PolicyModeType;
    }) => {
      if (!organizationId) {
        throw new Error('Organization ID is required');
      }
      const result = await toggleOrganizationPolicy(
        apiClient,
        organizationId,
        policyKey,
        enabled,
        mode
      );
      return result;
    },
    onSuccess: () => {
      // Invalidate policies list to refresh the enabled state
      if (organizationId) {
        queryClient.invalidateQueries({
          queryKey: queryKeys.policies.organization(organizationId),
        });
      }
    },
  });
}

/**
 * Hook to update organization policy settings
 *
 * Uses React Query mutation with automatic cache invalidation.
 * Only available to owners and managers.
 *
 * @param organizationId - The organization ID
 *
 * @example
 * const { mutateAsync, isPending } = useUpdateOrganizationPolicySettings(orgId);
 * await mutateAsync({ policyKey: 'mcp-server-sources', settings: {...} });
 *
 * @returns React Query mutation result
 */
export function useUpdateOrganizationPolicySettings(
  organizationId: OrganizationId | undefined
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (settingsUpdate: PolicySettingsUpdate) => {
      if (!organizationId) {
        throw new Error('Organization ID is required');
      }
      const result = await updateOrganizationPolicySettings(
        apiClient,
        organizationId,
        settingsUpdate
      );
      return result;
    },
    onSuccess: (_, variables) => {
      if (organizationId) {
        // Invalidate the specific policy settings cache
        queryClient.invalidateQueries({
          queryKey: queryKeys.policies.organizationSettings(
            organizationId,
            variables.policyKey
          ),
        });
        // Also invalidate policies list to update customizedSettings count
        queryClient.invalidateQueries({
          queryKey: queryKeys.policies.organization(organizationId),
        });
      }
    },
  });
}

/**
 * Hook to reset organization policy settings (delete all or specific setting)
 *
 * Uses React Query mutation with automatic cache invalidation.
 * Only available to owners and managers.
 *
 * @param organizationId - The organization ID
 *
 * @example
 * const { mutateAsync, isPending } = useResetOrganizationPolicySettings(orgId);
 * await mutateAsync({ policyKey: 'mcp-server-sources', settingKey: 'trustedSources' });
 *
 * @returns React Query mutation result
 */
export function useResetOrganizationPolicySettings(
  organizationId: OrganizationId | undefined
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      policyKey,
      settingKey,
    }: {
      policyKey: string;
      settingKey?: string;
    }) => {
      if (!organizationId) {
        throw new Error('Organization ID is required');
      }
      const result = await deleteOrganizationPolicySettings(
        apiClient,
        organizationId,
        policyKey,
        settingKey
      );
      return result;
    },
    onSuccess: (_, variables) => {
      if (organizationId) {
        // Invalidate the specific policy settings cache
        queryClient.invalidateQueries({
          queryKey: queryKeys.policies.organizationSettings(
            organizationId,
            variables.policyKey
          ),
        });
        // Also invalidate policies list to update customizedSettings count
        queryClient.invalidateQueries({
          queryKey: queryKeys.policies.organization(organizationId),
        });
      }
    },
  });
}
